import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  ArrowLeft, 
  Filter, 
  RotateCcw, 
  Clock, 
  User, 
  Users, 
  Search, 
  Layers, 
  Hash, 
  Cpu
} from 'lucide-react';
import { OccurrenceItem, OccurrenceFilter } from '../../pages/settings/occurrences.types';
import { OccurrencesController } from '../../pages/settings/occurrences.controller';

interface OccurrencesViewProps {
  onBack: () => void;
}

export default function OccurrencesView({ onBack }: OccurrencesViewProps) {
  const [occurrences, setOccurrences] = useState<OccurrenceItem[]>([]);
  const [filteredOccurrences, setFilteredOccurrences] = useState<OccurrenceItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Estados dos filtros
  const [filters, setFilters] = useState<OccurrenceFilter>({
    orderNumber: '',
    sector: '',
    searchText: ''
  });

  const sectors = ['Corte', 'Dobra', 'Solda', 'Acabamento', 'Acessórios', 'Embalagem', 'Logística', 'Instalação'];

  const loadOccurrences = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await OccurrencesController.fetchOccurrences();
      setOccurrences(data);
      applyFilters(data, filters);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Erro ao carregar as ocorrências de produção.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOccurrences();
  }, []);

  const applyFilters = (data: OccurrenceItem[], currentFilters: OccurrenceFilter) => {
    let result = [...data];

    // Filtro por Número do Pedido (Ignora case e espaço)
    if (currentFilters.orderNumber.trim()) {
      const ordNumClean = currentFilters.orderNumber.trim().toLowerCase();
      result = result.filter(item => 
        item.orderNumber.toLowerCase().includes(ordNumClean)
      );
    }

    // Filtro por Setor
    if (currentFilters.sector) {
      result = result.filter(item => 
        item.sector.toLowerCase() === currentFilters.sector.toLowerCase()
      );
    }

    // Filtro por busca de texto ampla (Ocorrência, operadores, descrição)
    if (currentFilters.searchText.trim()) {
      const search = currentFilters.searchText.trim().toLowerCase();
      result = result.filter(item => 
        item.occurrences.toLowerCase().includes(search) ||
        item.productName.toLowerCase().includes(search) ||
        item.currentOperator.toLowerCase().includes(search) ||
        item.allCollaborators.toLowerCase().includes(search)
      );
    }

    setFilteredOccurrences(result);
  };

  const handleFilterChange = (key: keyof OccurrenceFilter, value: string) => {
    const updatedFilters = {
      ...filters,
      [key]: value
    };
    setFilters(updatedFilters);
    applyFilters(occurrences, updatedFilters);
  };

  const handleClearFilters = () => {
    const cleared: OccurrenceFilter = {
      orderNumber: '',
      sector: '',
      searchText: ''
    };
    setFilters(cleared);
    applyFilters(occurrences, cleared);
  };

  const formatEventDate = (dateStr: string) => {
    if (!dateStr) return 'Sem data';
    try {
      const normalized = dateStr.includes(' ') ? dateStr.replace(' ', 'T') : dateStr;
      const parsed = new Date(normalized);
      if (isNaN(parsed.getTime())) {
        return dateStr;
      }
      return parsed.toLocaleString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  // Cores de fundo e texto por setor do chão de fábrica
  const getSectorBadgeStyle = (sector: string) => {
    const s = sector.toLowerCase();
    if (s.includes('corte')) return 'bg-danger-subtle text-danger-emphasis';
    if (s.includes('dobra')) return 'bg-warning-subtle text-warning-emphasis';
    if (s.includes('solda')) return 'bg-info-subtle text-info-emphasis';
    if (s.includes('acabamento')) return 'bg-primary-subtle text-primary-emphasis';
    if (s.includes('acessorio') || s.includes('acessório')) return 'bg-success-subtle text-success-emphasis';
    if (s.includes('embalagem')) return 'bg-dark-subtle text-dark-emphasis';
    if (s.includes('logistica') || s.includes('logística')) return 'bg-secondary-subtle text-secondary-emphasis';
    return 'bg-secondary-subtle text-secondary-emphasis';
  };

  return (
    <div className="animate-fade-in py-1" id="occurrences-viewport">
      {/* Cabeçalho */}
      <div className="border-bottom pb-3 mb-4 d-flex align-items-center justify-content-between">
        <div>
          <h1 className="h4 fw-black text-dark font-display mb-1 d-flex align-items-center gap-2">
            <AlertTriangle size={22} className="text-warning animate-pulse" />
            Ocorrências do Chão de Fábrica
          </h1>
          <p className="text-muted text-xs font-mono mb-0">
            Monitoramento de notas de parada, atrasos e justificativas inseridas pelos operadores nos setores.
          </p>
        </div>
        <button 
          onClick={onBack}
          className="btn btn-outline-secondary btn-xs d-flex align-items-center gap-1.5 px-3 py-1.5 rounded-3 border-secondary-subtle"
          id="btn-back-settings-from-occurrences"
        >
          <ArrowLeft size={14} />
          <span>Voltar</span>
        </button>
      </div>

      {/* Exibição de Erros */}
      {error && (
        <div className="alert alert-danger border-0 rounded-3 shadow-xs mb-4" role="alert" id="occurrences-error-alert">
          {error}
        </div>
      )}

      {/* Filtros de Busca */}
      <div className="card border rounded-3 shadow-xs mb-4 bg-white" id="occurrences-filter-card">
        <div className="card-header bg-light border-bottom py-2.5 px-3">
          <span className="text-xs fw-bold text-dark font-sans d-flex align-items-center gap-1.5">
            <Filter size={14} className="text-muted" />
            Filtros Operacionais
          </span>
        </div>
        <div className="card-body p-3">
          <div className="row g-3" id="occurrences-filter-form">
            {/* Número do Pedido */}
            <div className="col-12 col-md-3">
              <label className="form-label text-xxs text-secondary fw-semibold font-mono mb-1">Nº DO PEDIDO</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light text-muted border-end-0"><Hash size={13} /></span>
                <input 
                  type="text"
                  className="form-control text-xs rounded-end-3"
                  placeholder="Ex: O-102"
                  value={filters.orderNumber}
                  onChange={(e) => handleFilterChange('orderNumber', e.target.value)}
                  id="input-filter-occ-order"
                />
              </div>
            </div>

            {/* Setor */}
            <div className="col-12 col-md-3">
              <label className="form-label text-xxs text-secondary fw-semibold font-mono mb-1">SETOR DO FLUXO</label>
              <select 
                className="form-select form-select-sm text-xs rounded-3"
                value={filters.sector}
                onChange={(e) => handleFilterChange('sector', e.target.value)}
                id="select-filter-occ-sector"
              >
                <option value="">Todos os Setores</option>
                {sectors.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Busca textual ampla */}
            <div className="col-12 col-md-4">
              <label className="form-label text-xxs text-secondary fw-semibold font-mono mb-1">BUSCA AMPLA</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light text-muted border-end-0"><Search size={13} /></span>
                <input 
                  type="text"
                  className="form-control text-xs rounded-end-3"
                  placeholder="Buscar por ocorrência, operador ou produto..."
                  value={filters.searchText}
                  onChange={(e) => handleFilterChange('searchText', e.target.value)}
                  id="input-filter-occ-search"
                />
              </div>
            </div>

            {/* Ações */}
            <div className="col-12 col-md-2 d-flex align-items-end justify-content-end">
              <button 
                type="button"
                onClick={handleClearFilters}
                className="btn btn-light btn-sm text-xs rounded-3 w-100 border py-2 d-flex align-items-center justify-content-center gap-1.5"
                id="btn-clear-occurrences-filter"
              >
                <RotateCcw size={13} />
                <span>Limpar Filtros</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Ocorrências */}
      <div className="card border rounded-3 shadow-xs bg-white mb-4" id="occurrences-table-card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" id="occurrences-data-table">
              <thead className="table-light">
                <tr className="border-bottom text-xxs text-secondary font-mono">
                  <th className="py-2.5 px-3" style={{ width: '160px' }}>DATA / HORA</th>
                  <th className="py-2.5 px-3" style={{ width: '180px' }}>IDENTIFICAÇÃO</th>
                  <th className="py-2.5 px-3" style={{ width: '120px' }}>SETOR</th>
                  <th className="py-2.5 px-3">OCORRÊNCIA REGISTRADA</th>
                  <th className="py-2.5 px-3" style={{ width: '250px' }}>OPERADORES ENVOLVIDOS</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted font-mono text-xs">
                      <div className="spinner-border spinner-border-sm me-2 text-warning" role="status"></div>
                      Buscando registros de ocorrências lançadas...
                    </td>
                  </tr>
                ) : filteredOccurrences.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted font-sans text-xs">
                      Nenhuma ocorrência operacional encontrada para os filtros aplicados.
                    </td>
                  </tr>
                ) : (
                  filteredOccurrences.map((occ) => {
                    return (
                      <tr key={occ.id} className="border-bottom hover-bg-light transition-all text-xs" id={`occurrence-row-${occ.id}`}>
                        {/* Data / Hora */}
                        <td className="py-2.5 px-3 font-mono text-muted">
                          <div className="d-flex align-items-center gap-1.5">
                            <Clock size={12} className="text-muted" />
                            {formatEventDate(occ.createdAt)}
                          </div>
                        </td>

                        {/* Pedido e Item */}
                        <td className="py-2.5 px-3">
                          <div className="fw-black text-dark font-mono text-xs">
                            Pedido #{occ.orderNumber}
                          </div>
                          <div className="text-muted text-xxs d-flex align-items-center gap-1 mt-0.5">
                            <Cpu size={11} className="text-muted" />
                            <span>Item {String(occ.itemIndex).padStart(2, '0')} - {occ.productName}</span>
                          </div>
                        </td>

                        {/* Setor */}
                        <td className="py-2.5 px-3">
                          <span className={`badge ${getSectorBadgeStyle(occ.sector)} fw-semibold text-xxs font-mono px-2 py-1 rounded`}>
                            {occ.sector}
                          </span>
                        </td>

                        {/* Texto da Ocorrência */}
                        <td className="py-2.5 px-3">
                          <div className="bg-light-subtle text-dark border-start border-3 border-warning p-2 rounded-end text-xs font-sans fw-medium shadow-xxs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {occ.occurrences}
                          </div>
                        </td>

                        {/* Operadores Envolvidos (Quem lançou e Acumulados) */}
                        <td className="py-2.5 px-3">
                          <div className="d-flex align-items-center gap-1.5 mb-1 text-xs text-dark">
                            <User size={12} className="text-secondary" />
                            <span className="fw-semibold">{occ.currentOperator}</span>
                            <span className="text-xxs text-muted font-mono">(Lançou)</span>
                          </div>
                          {occ.allCollaborators && occ.allCollaborators.split(', ').length > 1 && (
                            <div className="text-xxs text-muted bg-light p-1.5 rounded-3 d-flex align-items-start gap-1">
                              <Users size={11} className="text-muted mt-0.5 flex-shrink-0" />
                              <div className="font-mono">
                                <span className="fw-bold d-block text-secondary text-xxs mb-0.5">HISTÓRICO ACUMULADO:</span>
                                {occ.allCollaborators}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
